import React, { useEffect, useState } from 'react';
import api from '../../../../api';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import CountUp from 'react-countup';

const EmployeeEfficiency = ({ filters }) => {
  const { accessToken } = useAuth();
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const [focusMode, setFocusMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setCurrentPage(1);
        const baseUrl = getRoundRobinBaseUrl();
        const params = {};
        Object.keys(filters).forEach((key) => {
          if (filters[key]) params[key] = filters[key];
        });
        const res = await api.get(`${baseUrl}/superAdminDashboard/getEmployeeWiseEfficiency`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });
        const data = res.data?.data?.scopeEfficiency || [];
        setEmployeeData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Fetch error:', err);
        setEmployeeData([]);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchData();
  }, [accessToken, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredData = employeeData.filter((d) => d.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()));

  const sortedData = React.useMemo(() => {
    const sorted = [...filteredData];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        return typeof aVal === 'string'
          ? sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal)
          : sortConfig.direction === 'asc'
          ? aVal - bVal
          : bVal - aVal;
      });
    }
    return sorted;
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = sortedData.slice(indexOfFirst, indexOfLast);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    else if (sortConfig.key === key && sortConfig.direction === 'desc') direction = '';
    setSortConfig({ key: direction ? key : '', direction });
  };

  const getSortIcon = (key) => {
    const base = 'w-4 h-4';
    if (sortConfig.key !== key) return <ChevronUpDownIcon className={`${base} text-gray-300`} />;
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className={`${base}  text-[#edeeef]`} />
    ) : (
      <ChevronDownIcon className={`${base}  text-[#edeeef]`} />
    );
  };

  const handleExportExcel = () => {
    const rows = employeeData.map((d) => ({
      'Employee Name': d.employeeName,
      'Efficiency (%)': d.efficiencyPercentage,
      'Biometric Hours': d.biometricHours,
      'EPR Hours': d.eprHours,
      'Quality (%)': d.qualityPercentage,
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Efficiency');
    XLSX.writeFile(wb, 'EmployeeEfficiency.xlsx');
  };

  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5; // control how many page buttons to show
    const pageWindow = 1; // how many pages before/after current

    // Prev
    pages.push(
      <button
        key="prev"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
      >
        {'<'}
      </button>
    );

    const pageList = [];

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 || // always show first
        i === totalPages || // always show last
        (i >= currentPage - pageWindow && i <= currentPage + pageWindow)
      ) {
        pageList.push(i);
      } else if ((i === currentPage - pageWindow - 1 && i > 1) || (i === currentPage + pageWindow + 1 && i < totalPages)) {
        pageList.push('...');
      }
    }

    pageList.forEach((p, idx) => {
      if (p === '...') {
        pages.push(
          <span key={`dots-${idx}`} className="px-2 text-gray-400">
            ...
          </span>
        );
      } else {
        pages.push(
          <button
            key={p}
            onClick={() => setCurrentPage(p)}
            className={`px-3 py-1 text-sm rounded-full border font-semibold transition ${
              currentPage === p ? 'bg-[#1A2B5C] text-white border-[#1A2B5C]' : 'text-gray-600 border-gray-300 hover:bg-gray-100'
            }`}
          >
            {p}
          </button>
        );
      }
    });

    // Next
    pages.push(
      <button
        key="next"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
      >
        {'>'}
      </button>
    );

    return (
      <>
        {/* Showing message aligned to left */}
        {sortedData.length > 0 && (
          <div className="mt-4 text-sm text-gray-600 text-left">
            Showing {indexOfFirst + 1} to {Math.min(indexOfLast, sortedData.length)} of {sortedData.length} entries
          </div>
        )}

        {/* Pagination controls centered below */}
        <div className="flex flex-wrap justify-center items-center gap-2 mt-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {'<'}
          </button>
          {pages}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {'>'}
          </button>
        </div>
      </>
    );
  };

  const topThree = [...employeeData]
    .sort((a, b) => parseFloat(b.efficiencyPercentage) - parseFloat(a.efficiencyPercentage))
    .slice(0, 3);

  return (
    <motion.div
      className="bg-white/60 backdrop-blur-xl border rounded-2xl ml-4 shadow-xl p-6 relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* <div className="absolute inset-0 rounded-2xl border-4 border-transparent bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] opacity-10 pointer-events-none" /> */}

      <div className="flex justify-between items-center mb-6 ml-4">
        <h3 className="text-2xl font-bold text-[#1A2B5C]">Employee Efficiency</h3>
        <div className="flex gap-3">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by scope name..."
              className="border border-gray-300 rounded-full pl-4 pr-10 py-1.5 text-sm w-full focus:ring-2 focus:ring-[#1A2B5C] outline-none shadow"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 text-lg font-bold"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>

          <motion.button
            whileHover={{ scale: employeeData.length ? 1.05 : 1 }}
            whileTap={{ scale: employeeData.length ? 0.95 : 1 }}
            onClick={() => employeeData.length > 0 && setFocusMode(true)}
            disabled={employeeData.length === 0}
            className={`px-4 py-2 text-sm rounded-full font-semibold shadow
    ${employeeData.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#1A2B5C] text-white hover:opacity-90'}
  `}
          >
            Top 3
          </motion.button>
          <motion.button
            whileHover={{ scale: employeeData.length ? 1.05 : 1 }}
            whileTap={{ scale: employeeData.length ? 0.95 : 1 }}
            onClick={handleExportExcel}
            disabled={employeeData.length === 0}
            className={`px-4 py-2 text-sm rounded-full font-semibold shadow
    ${
      employeeData.length === 0
        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
        : 'bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] text-white hover:opacity-90'
    }
  `}
          >
            Export to Excel
          </motion.button>
        </div>
      </div>

      {loading ? (
        <div className="min-h-[300px] flex justify-center items-center">
          <div className="animate-spin w-10 h-10 border-4 border-[#1A2B5C] border-t-[#EE1D23] rounded-full" />
        </div>
      ) : (
        <>
          <div className="overflow-auto rounded-xl bg-white shadow">
            <table className="min-w-full text-sm text-gray-800 whitespace-nowrap">
              <thead className="sticky top-0 z-10 bg-[#1A2B5C] text-white">
                <tr className="text-left">
                  {[
                    { key: 'employeeName', label: 'Name' },
                    { key: 'efficiencyPercentage', label: 'Efficiency' },
                    { key: 'biometricHours', label: 'Biometric Hrs' },
                    { key: 'eprHours', label: 'EPR Hrs' },
                    { key: 'qualityPercentage', label: 'Quality %' },
                  ].map(({ key, label }) => (
                    <th key={key} onClick={() => handleSort(key)} className="px-4 py-2 font-semibold cursor-pointer">
                      <div className="flex items-center gap-2">
                        {label}
                        {getSortIcon(key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="py-16 animate-glowIn">
                        <div className="flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-white via-[#f3f6fa] to-white/60 rounded-2xl px-6 py-10 shadow-inner border border-[#1A2B5C]/10 max-w-md mx-auto">
                          <div className="relative">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-[#1A2B5C] to-[#EE1D23] flex items-center justify-center shadow-md">
                              <svg
                                className="w-8 h-8 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={1.8}
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M12 8v4l3 3m5-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <div className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full px-2 py-0.5 text-xs font-bold text-[#EE1D23] shadow">
                              !
                            </div>
                          </div>
                          <h4 className="text-xl font-bold text-[#1A2B5C] tracking-wide">No Employee Efficiency Data</h4>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentData.map((d, i) => (
                    <motion.tr
                      key={d.employeeId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="bg-white hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-2 font-medium text-gray-800">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#1A2B5C] rounded-full text-white flex items-center justify-center text-xs font-bold">
                            {d.employeeName
                              .split(' ')
                              .map((w) => w[0])
                              .join('')
                              .slice(0, 2)}
                          </div>
                          {d.employeeName}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                            parseFloat(d.efficiencyPercentage) >= 80
                              ? 'bg-green-100 text-green-700'
                              : parseFloat(d.efficiencyPercentage) >= 50
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {d.efficiencyPercentage}
                        </span>
                      </td>
                      <td className="px-4 py-2">{d.biometricHours}</td>
                      <td className="px-4 py-2">{d.eprHours}</td>
                      <td className="px-4 py-2">{d.qualityPercentage}</td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination />
        </>
      )}

      {/* Focus Mode for Top 3 */}
      <AnimatePresence>
        {focusMode && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, transition: { type: 'spring', stiffness: 120, damping: 12 } }}
              exit={{ scale: 0.7, opacity: 0 }}
              className="bg-white rounded-3xl px-8 py-10 w-full max-w-5xl shadow-2xl relative"
            >
              <button
                onClick={() => setFocusMode(false)}
                className="absolute top-3 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold"
              >
                ×
              </button>

              <h4 className="text-2xl font-extrabold text-[#1A2B5C] mb-10 text-center tracking-wide">🎉 Top 3 Performers 🎉</h4>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                {topThree.map((emp, i) => {
                  const efficiency = parseFloat(emp.efficiencyPercentage);
                  const quality = parseFloat(emp.qualityPercentage);
                  const biometric = parseFloat(emp.biometricHours);

                  let badgeColor = 'bg-red-100 text-red-700';
                  if (efficiency >= 80) badgeColor = 'bg-green-100 text-green-700';
                  else if (efficiency >= 50) badgeColor = 'bg-yellow-100 text-yellow-700';

                  const rankBadge = [
                    { label: '🥇', color: 'text-yellow-500' },
                    { label: '🥈', color: 'text-gray-500' },
                    { label: '🥉', color: 'text-orange-400' },
                  ][i];

                  return (
                    <motion.div
                      key={emp.employeeId}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.2 }}
                      className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-xl border-2 border-[#1A2B5C]/10 hover:scale-105 transition-transform duration-300 relative"
                    >
                      {/* Avatar + Rank */}
                      <div className="relative">
                        <div className="w-20 h-20 bg-gradient-to-tr from-[#1A2B5C] to-[#EE1D23] rounded-full text-white flex items-center justify-center text-2xl font-bold shadow-inner ring-4 ring-offset-2 ring-[#1A2B5C]">
                          {emp.employeeName
                            .split(' ')
                            .map((w) => w[0])
                            .join('')
                            .slice(0, 2)}
                        </div>
                        <div className={`absolute -top-3 -right-3 text-2xl ${rankBadge.color}`}>{rankBadge.label}</div>
                      </div>

                      {/* Name */}
                      <div className="mt-4 text-lg font-semibold text-[#1A2B5C]">{emp.employeeName}</div>

                      {/* Efficiency Badge with CountUp */}
                      <div className={`mt-2 px-3 py-1 rounded-full text-xs font-bold ${badgeColor}`}>
                        Efficiency: <CountUp end={efficiency} decimals={2} suffix="%" duration={1.5} />
                      </div>

                      {/* Extra Stats with Progress Bars */}
                      <div className="mt-3 text-sm w-full px-4">
                        <div className="text-gray-600">Quality</div>
                        <div className="w-full bg-gray-100 h-2 rounded-full">
                          <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${quality}%` }} />
                        </div>
                        <div className="text-xs text-blue-700 font-bold mt-1">
                          <CountUp end={quality} decimals={2} suffix="%" duration={1.2} />
                        </div>

                        <div className="text-gray-600 mt-3">Biometric Hours</div>
                        <div className="w-full bg-gray-100 h-2 rounded-full">
                          <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${(biometric / 40) * 100}%` }} />
                        </div>
                        <div className="text-xs text-purple-700 font-bold mt-1">
                          <CountUp end={biometric} decimals={2} suffix=" hrs" duration={1.2} />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EmployeeEfficiency;
